// Residents: units (with their rooms) and tenants. Both lists page on the
// server, so the store keeps the current page and the paging numbers; the
// pages refetch with their own filters after a write.
import {
  listUnits,
  createUnit,
  updateUnit,
  deleteUnit,
  createRoom,
  updateRoom,
  deleteRoom,
} from '../../api/coLiving/units';
import {
  listTenants,
  createTenant,
  updateTenant,
  deleteTenant,
} from '../../api/coLiving/tenants';
import { listAllPages } from '../../api/coLiving/pagination';

export const state = {
  units: [],
  unitMeta: { page: 1, pageSize: 10, total: 0 },
  tenants: [],
  tenantMeta: { page: 1, pageSize: 10, total: 0 },
  uiFlags: { isFetchingUnits: false, isFetchingTenants: false },
};

export const getters = {
  getUnits: s => s.units,
  getUnitMeta: s => s.unitMeta,
  getTenants: s => s.tenants,
  getTenantMeta: s => s.tenantMeta,
  getUiFlags: s => s.uiFlags,
};

export const mutations = {
  setUnits(s, { results, page, page_size: pageSize, total }) {
    s.units = results;
    s.unitMeta = { page, pageSize, total };
  },
  setTenants(s, { results, page, page_size: pageSize, total }) {
    s.tenants = results;
    s.tenantMeta = { page, pageSize, total };
  },
  setUiFlag(s, { key, value }) {
    s.uiFlags = { ...s.uiFlags, [key]: value };
  },
};

// Only the latest request of each list may write it (see coLivingTickets).
const latest = { units: 0, tenants: 0 };

async function fetchLatest(commit, list, flag, load, mutation) {
  latest[list] += 1;
  const request = latest[list];
  commit('setUiFlag', { key: flag, value: true });
  try {
    const { data } = await load();
    if (request === latest[list]) commit(mutation, data);
    return data;
  } finally {
    if (request === latest[list]) {
      commit('setUiFlag', { key: flag, value: false });
    }
  }
}

export const actions = {
  fetchUnits({ commit }, query) {
    return fetchLatest(
      commit,
      'units',
      'isFetchingUnits',
      () => listUnits(query),
      'setUnits'
    );
  },
  // Every unit of one condo (all pages), for the tenant form's unit select.
  fetchAllUnits(_, condoId) {
    return listAllPages(listUnits, { condo_id: condoId });
  },
  async addUnit(_, fields) {
    const { data } = await createUnit(fields);
    return data;
  },
  async editUnit(_, { id, fields }) {
    const { data } = await updateUnit(id, fields);
    return data;
  },
  async removeUnit(_, id) {
    await deleteUnit(id);
  },
  async addRoom(_, { unitId, fields }) {
    const { data } = await createRoom(unitId, fields);
    return data;
  },
  async editRoom(_, { unitId, roomId, fields }) {
    const { data } = await updateRoom(unitId, roomId, fields);
    return data;
  },
  async removeRoom(_, { unitId, roomId }) {
    await deleteRoom(unitId, roomId);
  },
  fetchTenants({ commit }, query) {
    return fetchLatest(
      commit,
      'tenants',
      'isFetchingTenants',
      () => listTenants(query),
      'setTenants'
    );
  },
  // The Condo Details page shows tenants of one condo: empty the list first so
  // another page's rows are not shown while it loads.
  clearTenants({ commit }) {
    commit('setTenants', { results: [], page: 1, page_size: 10, total: 0 });
  },
  async addTenant(_, fields) {
    const { data } = await createTenant(fields);
    return data;
  },
  async editTenant(_, { id, fields }) {
    const { data } = await updateTenant(id, fields);
    return data;
  },
  // Answers `{id, result: 'deleted' | 'archived'}`.
  async removeTenant(_, id) {
    const { data } = await deleteTenant(id);
    return data;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
